import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  InputAdornment,
  Pagination,
  Grid,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import {
  userManagementService,
  AdminUser,
  BrandAssignment,
  AdminBrand,
} from '../../services/api/userManagement.service';

interface BrandAssignmentForm {
  brand_id: string;
  brand_name: string;
  manager_name: string;
  position_type: string;
  job_title: string;
  outlet_id: string;
}

const emptyAssignment: BrandAssignmentForm = {
  brand_id: '',
  brand_name: '',
  manager_name: '',
  position_type: 'GM',
  job_title: '',
  outlet_id: '',
};

const UserManagementPage: React.FC = () => {
  // Data state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [brands, setBrands] = useState<AdminBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Pagination & filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterPositionType, setFilterPositionType] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  // Form state
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formIsAdmin, setFormIsAdmin] = useState(false);
  const [formAssignments, setFormAssignments] = useState<BrandAssignmentForm[]>([{ ...emptyAssignment }]);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);

  // Context menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<AdminUser | null>(null);

  const isReadOnly = dialogMode === 'view';

  // Load users
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userManagementService.getUsers({
        page,
        limit: 10,
        search: searchQuery || undefined,
        brand_id: filterBrand || undefined,
        position_type: filterPositionType || undefined,
      });
      setUsers(response.data);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, filterBrand, filterPositionType]);

  // Load brands for dropdown
  const loadBrands = useCallback(async () => {
    try {
      const response = await userManagementService.getBrands();
      setBrands(response.data);
    } catch (err) {
      console.error('Failed to load brands:', err);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  // Auto-clear success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Context menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: AdminUser) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuUser(null);
  };

  // Dialog handlers
  const openCreateDialog = () => {
    setDialogMode('create');
    setFormEmail('');
    setFormPassword('');
    setFormIsAdmin(false);
    setFormAssignments([{ ...emptyAssignment }]);
    setSelectedUser(null);
    setDialogOpen(true);
  };

  const openEditDialog = (user: AdminUser) => {
    setDialogMode('edit');
    setSelectedUser(user);
    setFormEmail(user.email);
    setFormPassword('');
    setFormIsAdmin(user.is_admin || false);
    setFormAssignments(
      user.brand_assignments.length > 0
        ? user.brand_assignments.map((a) => ({
            brand_id: a.brand_id,
            brand_name: a.brand_name,
            manager_name: a.manager_name,
            position_type: a.position_type,
            job_title: a.job_title,
            outlet_id: a.outlet_id || '',
          }))
        : [{ ...emptyAssignment }]
    );
    setDialogOpen(true);
  };

  const openViewDialog = (user: AdminUser) => {
    setDialogMode('view');
    setSelectedUser(user);
    setFormEmail(user.email);
    setFormPassword('');
    setFormIsAdmin(user.is_admin || false);
    setFormAssignments(
      user.brand_assignments.length > 0
        ? user.brand_assignments.map((a) => ({
            brand_id: a.brand_id,
            brand_name: a.brand_name,
            manager_name: a.manager_name,
            position_type: a.position_type,
            job_title: a.job_title,
            outlet_id: a.outlet_id || '',
          }))
        : [{ ...emptyAssignment }]
    );
    setDialogOpen(true);
  };

  // Assignment handlers
  const addAssignment = () => {
    setFormAssignments([...formAssignments, { ...emptyAssignment }]);
  };

  const removeAssignment = (index: number) => {
    if (formAssignments.length > 1) {
      setFormAssignments(formAssignments.filter((_, i) => i !== index));
    }
  };

  const updateAssignment = (index: number, field: keyof BrandAssignmentForm, value: string) => {
    const updated = [...formAssignments];
    updated[index] = { ...updated[index], [field]: value };

    // Auto-fill brand_name when brand_id changes
    if (field === 'brand_id') {
      const brand = brands.find((b) => b.brand_id === value);
      if (brand) {
        updated[index].brand_name = brand.brand_name;
      }
    }

    setFormAssignments(updated);
  };

  // Save handler
  const handleSave = async () => {
    if (!formEmail) {
      setError('Email is required');
      return;
    }
    if (dialogMode === 'create' && !formPassword) {
      setError('Password is required');
      return;
    }

    // Filter out empty assignments
    const validAssignments = formAssignments.filter((a) => a.brand_id);

    setLoading(true);
    setError(null);
    try {
      if (dialogMode === 'create') {
        await userManagementService.createUser({
          email: formEmail,
          password: formPassword,
          is_admin: formIsAdmin,
          brand_assignments: validAssignments,
        });
        setSuccessMessage('User created successfully');
      } else if (dialogMode === 'edit' && selectedUser) {
        await userManagementService.updateUser(selectedUser.id, {
          email: formEmail,
          password: formPassword || undefined,
          is_admin: formIsAdmin,
          brand_assignments: validAssignments,
        });
        setSuccessMessage('User updated successfully');
      }
      setDialogOpen(false);
      loadUsers();
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to save user';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!userToDelete) return;

    setLoading(true);
    setError(null);
    try {
      await userManagementService.deleteUser(userToDelete.id);
      setSuccessMessage('User deleted successfully');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (err) {
      setError('Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const getUniquePositionTypes = (assignments: BrandAssignment[]) => {
    return Array.from(new Set(assignments.map((a) => a.position_type)));
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={openCreateDialog}
        >
          Create User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by email..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Brand</InputLabel>
              <Select
                value={filterBrand}
                onChange={(e) => { setFilterBrand(e.target.value); setPage(1); }}
                label="Brand"
              >
                <MenuItem value="">All Brands</MenuItem>
                {brands.map((b) => (
                  <MenuItem key={b.brand_id} value={b.brand_id}>
                    {b.brand_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Position Type</InputLabel>
              <Select
                value={filterPositionType}
                onChange={(e) => { setFilterPositionType(e.target.value); setPage(1); }}
                label="Position Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GM">GM</MenuItem>
                <MenuItem value="OTL">OTL</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchQuery('');
                setFilterBrand('');
                setFilterPositionType('');
                setPage(1);
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>Brands</TableCell>
                <TableCell>Position Type</TableCell>
                <TableCell>Manager Name</TableCell>
                <TableCell>Job Title</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {user.is_admin ? (
                        <Tooltip title="Admin user">
                          <Chip
                            icon={<AdminIcon />}
                            label="Admin"
                            size="small"
                            color="error"
                            variant="filled"
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {user.brand_assignments.length > 0 ? (
                          user.brand_assignments.map((a, i) => (
                            <Chip
                              key={i}
                              label={a.brand_name || a.brand_id}
                              size="small"
                              color={a.brand_id === 'AB' ? 'primary' : 'default'}
                              variant={a.brand_id === 'AB' ? 'filled' : 'outlined'}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No brands
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {getUniquePositionTypes(user.brand_assignments).map((pt) => (
                          <Chip
                            key={pt}
                            label={pt}
                            size="small"
                            color={pt === 'GM' ? 'success' : pt === 'OTL' ? 'warning' : 'default'}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {user.brand_assignments[0]?.manager_name || '-'}
                    </TableCell>
                    <TableCell>
                      {user.brand_assignments[0]?.job_title || '-'}
                    </TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, user)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" py={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (menuUser) openViewDialog(menuUser);
            handleMenuClose();
          }}
        >
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuUser) openEditDialog(menuUser);
            handleMenuClose();
          }}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (menuUser) {
              setUserToDelete(menuUser);
              setDeleteDialogOpen(true);
            }
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create/Edit/View Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {dialogMode === 'create' ? 'Create User' : dialogMode === 'edit' ? 'Edit User' : 'View User'}
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Email"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
              disabled={isReadOnly}
              required
              fullWidth
              type="email"
            />
            <TextField
              label="Password"
              value={formPassword}
              onChange={(e) => setFormPassword(e.target.value)}
              disabled={isReadOnly}
              fullWidth
              type="password"
              required={dialogMode === 'create'}
              helperText={dialogMode === 'edit' ? 'Leave blank to keep current password' : ''}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formIsAdmin}
                  onChange={(e) => setFormIsAdmin(e.target.checked)}
                  disabled={isReadOnly}
                  color="error"
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <AdminIcon color={formIsAdmin ? 'error' : 'disabled'} fontSize="small" />
                  <Typography>Admin User</Typography>
                </Box>
              }
            />

            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={600}>
                Brand Assignments
              </Typography>
              {!isReadOnly && (
                <Button size="small" startIcon={<AddIcon />} onClick={addAssignment}>
                  Add Assignment
                </Button>
              )}
            </Box>

            {formAssignments.map((assignment, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Brand</InputLabel>
                      <Select
                        value={assignment.brand_id}
                        onChange={(e) => updateAssignment(index, 'brand_id', e.target.value)}
                        label="Brand"
                        disabled={isReadOnly}
                      >
                        <MenuItem value="AB">All Brands (AB)</MenuItem>
                        {brands.map((b) => (
                          <MenuItem key={b.brand_id} value={b.brand_id}>
                            {b.brand_name} ({b.brand_id})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Position Type</InputLabel>
                      <Select
                        value={assignment.position_type}
                        onChange={(e) => updateAssignment(index, 'position_type', e.target.value)}
                        label="Position Type"
                        disabled={isReadOnly}
                      >
                        <MenuItem value="GM">GM (General Manager)</MenuItem>
                        <MenuItem value="OTL">OTL (Outlet)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Manager Name"
                      value={assignment.manager_name}
                      onChange={(e) => updateAssignment(index, 'manager_name', e.target.value)}
                      disabled={isReadOnly}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Job Title"
                      value={assignment.job_title}
                      onChange={(e) => updateAssignment(index, 'job_title', e.target.value)}
                      disabled={isReadOnly}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Outlet ID"
                      value={assignment.outlet_id}
                      onChange={(e) => updateAssignment(index, 'outlet_id', e.target.value)}
                      disabled={isReadOnly}
                      helperText="Required for OTL position"
                    />
                  </Grid>
                  {!isReadOnly && formAssignments.length > 1 && (
                    <Grid item xs={12} md={4} display="flex" alignItems="center">
                      <Button
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => removeAssignment(index)}
                      >
                        Remove
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {isReadOnly ? 'Close' : 'Cancel'}
          </Button>
          {!isReadOnly && (
            <Button variant="contained" onClick={handleSave} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : dialogMode === 'create' ? 'Create' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{userToDelete?.email}</strong>?
            This will also remove all their brand assignments.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementPage;
